import { inject, Injectable } from '@angular/core';
import {
  ensureInitialized,
  fetchAndActivate,
  getBoolean,
  getString,
  RemoteConfig
} from '@angular/fire/remote-config';

import { Functions, httpsCallable } from '@angular/fire/functions';
import { DbService } from './db.service';

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private _remoteConfig = inject(RemoteConfig);
  private _functions = inject(Functions);
  private _dbService = inject(DbService);

  _remoteConfigInitialized = false;

  constructor() {
    this.init();
  }

  async init() {
    if (!this._remoteConfigInitialized) {
      await ensureInitialized(this._remoteConfig);
      await fetchAndActivate(this._remoteConfig);
      this._remoteConfigInitialized = true;
    }
  }

  async getBooleanConfig(key: string): Promise<boolean> {
    await this.init();
    return getBoolean(this._remoteConfig, key);
  }

  async getStringConfig(key: string): Promise<string> {
    await this.init();
    return getString(this._remoteConfig, key);
  }

  async getObjectConfig<T>(key: string): Promise<T> {
    await this.init();
    return JSON.parse((await getString(this._remoteConfig, key)) || '{}');
  }

  updateRetailRemoteConfigKey(key: string, value: any) {
    const payload = {
      config: {
        key,
        value
      }
    };

    return httpsCallable<any, boolean>(
      this._functions,
      'updateRetailRemoteConfigKey'
    )(payload).then((result) => {
      return result.data;
    });
  }

  // get config from firestore to avoid delay in remote config caching layer for updates to certain keys like slides
  getRemoteConfigData(key: string) {
    return this._dbService.read('MAIN', 'remote-configs').then((conf: any) => {
      return conf.parameters[key].defaultValue.value;
    });
  }

  getRemoteConfigBatch(keys: string[]) {
    return this._dbService.read('MAIN', 'remote-configs').then((conf: any) => {
      const resp = {};
      keys.forEach((k) => {
        resp[k] = JSON.parse(conf.parameters[k].defaultValue.value);
      });
      return resp;
    });
  }
}
